import {keyframes} from '@emotion/react'
import {remCalc} from '_themeUtils'

const scrollBtnAnimation = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(8px);
    }
`

const zoomImgAnimation = keyframes`
    0% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
`

const enterTextAnimation = keyframes`
    from {
        transform: translateX(150%);
    }
    to {
        transform: translateX(0);
    }
`

// converts a percentual based ratio to an aspect ratio string
// e.g. "127.333" -> "1 / 1.273"
const convertRatio = (percent: string, isTextBelowImage: boolean = false): string => {
    const ratio = Number(percent) / 100
    return `${isTextBelowImage ? '0.8' : '1'} / ${ratio.toFixed(3)}`
}

const getAlignment = (textAlign): string => {
    if (textAlign === 'left') return 'flex-start'
    if (textAlign === 'right') return 'flex-end'
    return 'center'
}

const heroBanner = {
    HeroBanner: {
        baseStyle: ({
            padding,
            imgFullHeight,
            scrollArrowColor,
            colorContent,
            contentTextAlign,
            verticalAlign,
            horizontalAlign,
            isImgZoom,
            animationTime,
            ctaInlineDesk,
            ctaInlineMobile,
            imgDeskRatio,
            imgMobileRatio,
            imgTabletRatio,
            isAnimationTitle,
            isBannerHalf,
        }) => ({
            container: {
                position: 'relative',
                width: '100%',
                height: '100%',
                color: colorContent && colorContent.trim().length ? colorContent : 'color.text.white',
                justifyContent: 'center',
                overflow: 'hidden',
                ...(isBannerHalf && {
                    position: 'absolute',
                    h: '100%',
                }),
            },
            wrapper: {
                padding: padding == true ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : '0',
                w: '100%',
                gridColumn: '1 / -1', // Starts from the first column end ends at the last column
                height: imgFullHeight == true ? {base: `calc(100vh - ${remCalc(81)})`, md: `calc(100vh - ${remCalc(139)})`} : '100%',
                overflow: 'hidden',
                ...(isBannerHalf && {
                    position: 'relative',
                    aspectRatio: {base: imgMobileRatio ? convertRatio(imgMobileRatio) : '9 / 16', md: 'auto'},
                }),
            },
            image: {
                width: '100%',
                height: 0,
                pt:
                    imgDeskRatio || imgTabletRatio || imgMobileRatio
                        ? {
                              base: `${imgMobileRatio || imgTabletRatio || imgDeskRatio || 56.25}%`,
                              md: `${imgTabletRatio || imgDeskRatio || 56.25}%`,
                              xl: `${imgDeskRatio || 56.25}%`,
                          }
                        : '56.25%',
                minH: imgFullHeight ? {base: '60dvh', lg: '80dvh'} : 'unset',
                overflow: 'hidden',
                position: 'relative',

                ...(isBannerHalf && {
                    pt: 0,
                    h: '100%',
                }),

                img: {
                    objectPosition: isImgZoom ? 'center' : 'top',
                    animation: isImgZoom ? `${zoomImgAnimation} ${animationTime}ms cubic-bezier(.25,.1,.25,1)` : 'none',
                    transform: isImgZoom ? 'scale(1.2)' : 'none',
                },
            },
            video: {
                width: '100%',
                height: '0',
                pt: {base: '160%', md: '56.5%'},
                overflow: 'hidden',
                position: 'relative',
                ...(isBannerHalf && {
                    pt: 0,
                    h: '100%',
                }),
            },
            body: {
                position: 'absolute',
                gap: {base: 'spacing.5'},
                textAlign: contentTextAlign,
                alignItems: getAlignment(contentTextAlign),
                top: verticalAlign == 'top' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                bottom: verticalAlign == 'bottom' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                left: horizontalAlign == 'left' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                right: horizontalAlign == 'right' ? {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'} : 'unset',
                overflow: 'hidden',
                pointerEvents: 'all',
                maxW: {base: `calc(100% - ${remCalc(112)})`, md: `calc(100% - ${remCalc(64)})`, xl: `calc(100% - ${remCalc(48)})`},
                w: '100%',
            },
            bodyWrapper: {
                position: 'absolute',
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'color.background.alpha.dark.30',
                pointerEvents: 'none',
                zIndex: 1,
            },
            eyelet: {
                fontFamily: 'GTEesti',
                textStyle: 'paragraphSm',
                fontWeight: 'light',
                color: colorContent,
            },
            title: {
                textStyle: 'displaySm',
                fontFamily: 'GTEesti',
                fontWeight: 'light',
                animation: isAnimationTitle ? `${enterTextAnimation} ${animationTime}ms cubic-bezier(.25,.1,.25,1)` : 'none',
                color: colorContent,
            },
            text: {
                textStyle: 'paragraphMd',
                fontWeight: 350,
                color: colorContent,
            },
            timer: {
                pt: {base: remCalc(30), lg: remCalc(50)},
                pb: remCalc(30),
            },
            ctaContainer: {
                flexWrap: {base: 'wrap', lg: 'nowrap'},
                w: '100%',
                maxW: remCalc(700),
                alignItems: contentTextAlign === 'right' ? 'end' : contentTextAlign == 'left' ? 'start' : 'center',
                columnGap: {base: ctaInlineMobile ? 'spacing.12' : 0, md: ctaInlineDesk ? 'spacing.12' : 0},
                rowGap: 'spacing.6',
                flexDirection: {base: ctaInlineMobile ? 'row' : 'column', md: ctaInlineDesk ? 'row' : 'column'},
                justifyContent: 'center',
                color: colorContent,
                '.cc-content-button': {
                    mt: 0,
                },
                mt: {base: remCalc(16), md: 'unset'},
            },
            link: {
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: 3,
                _focus: {
                    outlineOffset: '-1px !important',
                },
            },
            scrollDown: {
                position: 'absolute',
                p: remCalc(7),
                background: 'transparent',
                bottom: {base: 'spacing.6', md: 'spacing.8', xl: 'spacing.16'},
                '.heroBanner__scroll__btn': {
                    color: scrollArrowColor,
                    background: 'color.background.transparent',
                    svg: {
                        animation: `${scrollBtnAnimation} infinite 0.75s alternate`,
                        minW: remCalc(50),
                        maxW: remCalc(50),
                        minH: remCalc(50),
                        maxH: remCalc(50),
                    },
                    _hover: {
                        color: scrollArrowColor,
                    },
                },
            },
        }),

        variants: {
            textBottomMobile: ({contentTextAlign, isBannerHalf, imgMobileRatio}) => ({
                container: {
                    flexDirection: 'column',
                },
                wrapper: {
                    ...(isBannerHalf && {
                        aspectRatio: {base: imgMobileRatio ? convertRatio(imgMobileRatio, true) : '9 / 21', md: 'auto'},
                    }),
                },
                body: {
                    position: 'relative',
                    top: 'unset',
                    bottom: 'unset',
                },
                bodyWrapper: {
                    position: 'relative',
                    background: 'color.background.transparent',
                    mt: 'spacing.12',
                    mb: 'spacing.8',
                    justifyContent: getAlignment(contentTextAlign),
                    ...(isBannerHalf && {
                        flex: 1,
                    }),
                },
            }),
        },
    },
}

export default heroBanner
